import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import React, { useEffect, useMemo, useState, useContext, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { useSelector } from 'react-redux';
import {
  clearCsvOvertimeReportData,
  clearPtoReportData,
  getCsvPayrollReport,
  getEmployeeList,
  getPayperiod,
  getPayrollReport,
} from '../../../store/ReportsDetailsSlice/actions.js';
import {
  selectCsvPayrollReportData,
  selectEmployeeData,
  selectPayperiodData,
  selectPayrollReportData,
} from '../../../store/ReportsDetailsSlice/selectors.js';
import { useDateRangeFilter } from '../hooks/useDateRangeFilter.jsx';
import { saveCsvToFile } from '../../../utils/saveCsvToFile.js';
import { usePaginationControls } from '../hooks/usePaginationControl.jsx';
import { GROUPBY } from '../../../constants/Reports';
import SelectRebuild from '../../../components/Inputs/SelectRebuild.jsx';
import { ExportCsvButton } from '../../../components/Buttons/ExportCsvButton.jsx';
import ReportsHeader from '../../../components/Reports/ReportsHeader';
import AuthContext from '../../../context/AuthContext.jsx';

const PAGINATION = 60;

const isDateString = (value) => {
  const date = Date.parse(value);
  return !Number.isNaN(date);
};

const createParams = (param) =>
  param && param.length > 0 ? param.map((par) => par.id) : ['ALL'];

/* ───────────────────────────────────────────────
   Fixed column widths
   ─────────────────────────────────────────────── */
const COL_WIDTHS = {
  date: '200px',
  shift: '200px',
  description: 'auto',
  charged_hours: '160px',
  hours: '160px',
  pay_code: '200px',
  name: '200px',
};

/* ───────────────────────────────────────────────
   Friendly absent-code labels
   ─────────────────────────────────────────────── */
const ABSENT_CODE_LABELS = {
  VAC: 'Vacation',
  VAC_WEEK: 'Vacation (Week)',
  SICK: 'Sick',
  SICK_SELF: 'Sick (Self)',
  SICK_FAM: 'Sick (Family)',
  PER: 'Personal',
  PER_SUPER: 'Personal (Super)',
  BRV: 'Bereavement',
  COMP: 'Comp Time',
  HOL: 'Holiday',
  MAT_PAT: 'Maternity / Paternity',
  MILITARY: 'Military',
  RET_BRD: 'Retirement Board',
  UNION: 'Union',
  DEPT: 'Department',
  JURY: 'Jury Duty',
};

const friendlyAbsentCode = (code) => {
  if (!code || code === 'No PTO' || code === 'SWAP') return '';
  return ABSENT_CODE_LABELS[code] || code.replace(/_/g, ' ');
};

const titleCase = (s) =>
  (s || '')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const isPlainRegular = (item) => {
  const rawType = item.type || '';
  const isRegular = rawType === 'regular' || rawType.toLowerCase().startsWith('regular');
  if (!isRegular) return false;
  // If type has parenthetical content (e.g. "Regular ( Floating Holiday )"), it's not plain
  if (rawType.match(/\(/)) return false;
  return (!item.pto || item.pto === 'No PTO') && !item.is_swap_off;
};

/* ───────────────────────────────────────────────
   Build description
   ─────────────────────────────────────────────── */
const getDescription = (item, alterLabels = false) => {
  const rawType = item.type || '';
  const isRegular = rawType === 'regular' || rawType.toLowerCase().startsWith('regular');

  if (isRegular) {
    if (item.is_swap_off) {
      return 'Swap Taken';
    }

    // Extract parenthetical info from type string e.g. "Regular ( Floating Holiday )"
    const parenMatch = rawType.match(/\(\s*([^)]+?)\s*\)/);
    if (parenMatch) return parenMatch[1].trim();

    if (!item.pto || item.pto === 'No PTO') return '';
    return friendlyAbsentCode(item.pto) || '';
  }

  if (item.type === 'custom_taken') {
    if (item.pto === 'SWAP') {
      return (item.overtime_shift_type || '').includes('Swap On') ? 'Swap Worked' : 'Swap Taken';
    }
    const label = friendlyAbsentCode(item.pto);
    const hasTime = item.start_time && item.end_time;
    const timeRange = hasTime
      ? ` ${item.start_time.slice(0, 5)} – ${item.end_time.slice(0, 5)}`
      : '';
    return label ? `${label}${timeRange}` : timeRange.trim();
  }

  if (item.type === 'included_hours') {
    return friendlyAbsentCode(item.pto) || 'Included Hours';
  }

  if (item.type === 'overtime') {
    const ost = item.overtime_shift_type || '';
    const dt = item.detail_type || '';

    if (ost === 'Detail' && dt) return `Detail – ${titleCase(dt)}`;
    if (ost === 'Detail') return 'Detail';
    if (ost === 'Payroll') {
      const extras = [item.reason, item.description].filter(Boolean);
      return extras.length ? `Payroll (${extras.join(' – ')})` : 'Payroll';
    }
    if (ost === 'Regular') {
      const origin = item.origin_name ? ` ${item.origin_name}` : '';
      return `OT Fill${origin}`;
    }
    if (ost === 'Swap on') {
      return 'Swap Worked';
    }
    if (ost === 'Schedule OT') return 'Schedule OT';
    return ost || 'Overtime';
  }

  return '';
};

const parseMMDDYYYY = (s) => {
  if (!s) return 0;
  const [mm, dd, yyyy] = String(s).split('/');
  return new Date(+yyyy, +mm - 1, +dd).getTime();
};

/* ───────────────────────────────────────────────
   SectionRows — renders inside shared <TableBody>
   ─────────────────────────────────────────────── */
const SectionRows = ({ items, headerItems, sectionLabel }) => {
  if (!items || items.length === 0) return null;

  const colCount = headerItems.length;
  const totalHours = items.reduce((sum, it) => {
    const h = it._displayHours;
    return h !== '' && h !== null && h !== undefined
      ? sum + (Number(h) || 0)
      : sum;
  }, 0);
  const totalChargedHours = items.reduce((sum, it) => {
    const h = it._chargedHours;
    return h !== '' && h !== null && h !== undefined
      ? sum + (Number(h) || 0)
      : sum;
  }, 0);

  return (
    <>
      <TableRow>
        <TableCell
          colSpan={colCount}
          sx={{
            fontWeight: 700,
            fontSize: '0.875rem',
            backgroundColor: '#f5f5f5',
            borderBottom: '2px solid #e0e0e0',
            py: 1,
          }}
        >
          {sectionLabel}
        </TableCell>
      </TableRow>

      {items.map((item, i) => (
        <TableRow
          key={`${sectionLabel}-row-${i}`}
          sx={{
            '&:nth-of-type(odd)': { backgroundColor: 'rgba(0,0,0,0.015)' },
          }}
        >
          {headerItems.map(([key], cIdx) => (
            <TableCell
              key={`${sectionLabel}-cell-${i}-${cIdx}`}
              sx={{ whiteSpace: 'nowrap' }}
            >
              <Typography fontSize={14} fontWeight={500}>
                {key === 'hours'
                  ? (item._displayHours ?? '')
                  : key === 'charged_hours'
                    ? (item._chargedHours ?? '')
                    : (item[key] ?? '')}
              </Typography>
            </TableCell>
          ))}
        </TableRow>
      ))}

      <TableRow>
        {headerItems.map(([key], cIdx) => {
          if (key === 'charged_hours') {
            return (
              <TableCell key={`${sectionLabel}-total-${cIdx}`}>
                <Typography fontSize={14} fontWeight={700}>
                  {totalChargedHours !== 0 ? +totalChargedHours.toFixed(2) : ''}
                </Typography>
              </TableCell>
            );
          }
          if (key === 'hours') {
            return (
              <TableCell key={`${sectionLabel}-total-${cIdx}`}>
                <Typography fontSize={14} fontWeight={700}>
                  {totalHours !== 0 ? +totalHours.toFixed(2) : ''}
                </Typography>
              </TableCell>
            );
          }
          if (cIdx === 0) {
            return (
              <TableCell key={`${sectionLabel}-total-${cIdx}`}>
                <Typography fontSize={13} fontWeight={600} color="text.secondary">
                  Total
                </Typography>
              </TableCell>
            );
          }
          return <TableCell key={`${sectionLabel}-total-${cIdx}`} />;
        })}
      </TableRow>
    </>
  );
};

/* ───────────────────────────────────────────────
   Component
   ─────────────────────────────────────────────── */
const ReportPayrollScreen = () => {
  const printRef = useRef(null);
  const dispatch = useDispatch();
  const [exportType, setExportType] = useState('');

  const [params, setParams] = useSearchParams();
  const groupByParam = params.get('groupby') || GROUPBY[0].value;
  const [appliedGroupBy, setAppliedGroupBy] = useState(groupByParam);
  const isTabletVerticalWidth = useMediaQuery('(max-width:1024px)');
  const isTabletWidth = useMediaQuery('(max-width:899px)');
  const { v3Data, department } = useContext(AuthContext);

  const { register, setValue, watch } = useForm({
    defaultValues: { emp_code: [] },
  });

  const { csvPayrollReport, loadCsvPayrollReport } = useSelector(
    selectCsvPayrollReportData
  );
  const { payrollReportData, loadingPayrollReport } = useSelector(
    selectPayrollReportData
  );
  const { payperiod } = useSelector(selectPayperiodData);

  /* ── Column headers ── */
  const headerItems = useMemo(() => {
    const base = [
      ['date', 'Date'],
      ['shift', 'Shift'],
      ['description', 'Description'],
      ['charged_hours', 'Charged Hours'],
      ['hours', 'Hours'],
      ['pay_code', 'Pay Code'],
    ];
    if (appliedGroupBy === 'Date') {
      return [['name', 'Name'], ...base.filter(([k]) => k !== 'date')];
    }
    return base;
  }, [appliedGroupBy]);

  /* ── Normalize item ── */
  const normalizeItem = (item, defaultPayCode) => {
    const description = getDescription(item, v3Data?.V3_ALLOW_ALTER_LABEL_TEXTS === 'true');
    const plain = isPlainRegular(item);

    const baseHours =
      item.type === 'custom_taken' && !item.is_include_hours
        ? Number(item.hours || 0) > 0
          ? -Number(item.hours)
          : Number(item.hours || 0)
        : Number(item.hours || 0);

    const isRegularType = (item.type || '').toLowerCase().startsWith('regular');
    // PTO-charged: regular with PTO, or custom_taken
    const isChargedType = (isRegularType && !plain) || item.type === 'custom_taken';

    const isSwap = item.is_swap_off || item.pto === 'SWAP';

    const base = {
      ...item,
      description,
      _displayHours: isSwap ? '' : (isChargedType || plain ? '' : baseHours),
      _chargedHours: isSwap ? '' : (isChargedType ? baseHours : ''),
    };

    if ((item.type || '').toLowerCase().startsWith('regular')) return base;

    if (item.type === 'included_hours') {
      return { ...base, shift: item.shift || '' };
    }

    if (item.type === 'custom_taken') {
      const hasTime = item.start_time && item.end_time;
      return {
        ...base,
        pay_code: item.pay_code || defaultPayCode,
        shift: hasTime
          ? `${item.start_time.slice(0, 5)} – ${item.end_time.slice(0, 5)}`
          : '',
      };
    }

    if (item.type === 'overtime') {
      const shiftValue = item.shift?.trim();
      const hasTime = item.start_time && item.end_time;

      if (item.overtime_shift_type === 'Regular') {
        if (shiftValue === 'Day' || shiftValue === 'Night')
          return { ...base, shift: shiftValue };
        if (!shiftValue && hasTime)
          return {
            ...base,
            shift: `${item.start_time.slice(0, 5)} – ${item.end_time.slice(0, 5)}`,
          };
      }

      if (item.overtime_shift_type === 'Swap on')
        return { ...base, shift: shiftValue || '' };

      let shiftText = '';
      if (shiftValue) shiftText = shiftValue;
      else if (hasTime)
        shiftText = `${item.start_time.slice(0, 5)} – ${item.end_time.slice(0, 5)}`;

      return { ...base, shift: shiftText };
    }

    return base;
  };

  /* ── Build table items ── */
  const tableItems = useMemo(() => {
    return Object.entries(payrollReportData?.data || {})
      .sort(([keyA], [keyB]) => {
        if (isDateString(keyA) && isDateString(keyB))
          return new Date(keyA) - new Date(keyB);
        return 0;
      })
      .map(([key, value]) => {
        const defaultPayCode =
          (value?.regular && Object.values(value.regular)?.[0]?.pay_code) ||
          (value?.overtime && value.overtime?.[0]?.pay_code) ||
          '';

        const shiftOrder = (shift) => {
          const s = (shift || '').toLowerCase();
          if (s === 'day') return 0;
          if (s === 'night') return 1;
          return 2;
        };
        const sortByDate = (arr) =>
          arr.sort((a, b) => {
            const dateDiff = parseMMDDYYYY(a.date) - parseMMDDYYYY(b.date);
            if (dateDiff !== 0) return dateDiff;
            return shiftOrder(a.shift) - shiftOrder(b.shift);
          });

        const scheduled = [];
        if (value.regular)
          scheduled.push(
            ...Object.values(value.regular).map((it) =>
              normalizeItem(it, defaultPayCode)
            )
          );
        if (value.included_hours)
          scheduled.push(
            ...value.included_hours.map((it) =>
              normalizeItem(it, defaultPayCode)
            )
          );
        if (value.custom_taken)
          scheduled.push(
            ...value.custom_taken.map((it) =>
              normalizeItem(it, defaultPayCode)
            )
          );
        sortByDate(scheduled);

        const unscheduled = [];
        if (value.overtime)
          unscheduled.push(
            ...value.overtime.map((it) =>
              normalizeItem(it, defaultPayCode)
            )
          );
        sortByDate(unscheduled);

        return [key, { scheduled, unscheduled }];
      });
  }, [payrollReportData]);

  const pagesCount = Math.ceil(Object.keys(tableItems).length / PAGINATION);
  const { fromDate, toDate, render, dateRange } = useDateRangeFilter(
    false,
    payperiod
  );
  const { employeeList, loadingEmployeeList } = useSelector(selectEmployeeData);
  const { emp_code: employees } = watch();
  const employeeParam = useMemo(() => createParams(employees), [employees]);

  const { paginationComponent, pageParam } = usePaginationControls({
    loading: loadingPayrollReport,
    pagesCount,
  });

  const tableData = useMemo(() => {
    return tableItems.slice(
      (+pageParam - 1) * PAGINATION,
      +pageParam * PAGINATION
    );
  }, [tableItems, pageParam]);

  const param = useMemo(() => {
    return {
      start_dt: moment(fromDate).format('YYYY-MM-DD') + ' 00:00:00',
      end_dt: moment(toDate).format('YYYY-MM-DD') + ' 23:59:59',
      sel_order: groupByParam,
      emp_code:
        employeeParam.length === 1 && employeeParam[0] === 'ALL'
          ? undefined
          : employeeParam,
      period_selection: dateRange === 'lastPay' ? 'last_pay_peroid' : undefined,
    };
  }, [fromDate, toDate, groupByParam, employeeParam, dateRange]);

  const handleChangeGroupBy = (e) => {
    const { target: { value } } = e;
    setParams((prev) => {
      if (!value) prev.delete('groupby');
      else {
        if (value === 'Group') prev.delete('employee');
        prev.set('groupby', value);
      }
      return prev;
    });
    dispatch(clearPtoReportData());
  };

  const renderGroupByValue = (selected) =>
    GROUPBY.find((it) => it.value === selected)?.title || '';

  const handleDownloadReport = (type) => {
    setExportType(type);
    dispatch(getCsvPayrollReport({ ...param, [type]: 1 }));
  };

  const handleFindClick = () => {
    setAppliedGroupBy(groupByParam);
    dispatch(getPayrollReport(param));
  };

  useEffect(() => {
    dispatch(getPayrollReport(param));
    dispatch(getEmployeeList());
    return () => dispatch(clearPtoReportData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setParams((prev) => { prev.delete('page'); return prev; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = () => {
    setAppliedGroupBy(groupByParam);
    dispatch(getPayrollReport(param));
  };

  useEffect(() => {
    if (v3Data?.V3_PAY_PERIOD_START) dispatch(getPayperiod());
  }, [department, dispatch]);

  const [shouldSaveCsv, setShouldSaveCsv] = useState(false);

  const handleDownload = () => {
    setShouldSaveCsv(true);
    dispatch(getCsvPayrollReport({ ...param, payroll_csv: 1 }));
  };

  const handlePrintSection = () => {
    const el = printRef.current;
    if (!el) return;

    // Clone content so we don't affect the live DOM
    const clone = el.cloneNode(true);
    const container = document.createElement('div');
    container.id = 'payroll-print-container';
    container.appendChild(clone);
    document.body.appendChild(container);

    const style = document.createElement('style');
    style.dataset.printPayroll = '1';
    style.innerHTML = `
      @media screen {
        #payroll-print-container { display: none; }
      }
      @media print {
        @page { size: landscape; margin: 20mm 25mm; }

        /* Hide everything except our clone */
        body > *:not(#payroll-print-container) { display: none !important; }

        #payroll-print-container {
          display: block !important;
          width: 100%;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }

        /* Fit tables to page */
        table {
          width: 100% !important;
          table-layout: auto !important;
          border-collapse: collapse !important;
        }
        .MuiTableCell-root {
          font-size: 10px !important;
          padding: 4px 6px !important;
          white-space: normal !important;
          overflow-wrap: anywhere !important;
          word-break: normal !important;
        }
        /* Force description column to have a real width so Chrome doesn't collapse it */
        th.MuiTableCell-root:nth-child(3),
        td.MuiTableCell-root:nth-child(3) {
          width: 150px !important;
          min-width: 150px !important;
        }
        .MuiTypography-root {
          font-size: 10px !important;
        }
        .MuiPaper-root {
          box-shadow: none !important;
        }
        h6, .MuiTypography-h6 {
          font-size: 13px !important;
          margin-bottom: 4px !important;
        }
      }
    `;
    document.head.appendChild(style);

    window.onafterprint = () => {
      style.remove();
      container.remove();
      window.onafterprint = null;
    };

    window.print();
  };

  // ─── Effects ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (csvPayrollReport?.data) {
      saveCsvToFile(csvPayrollReport.data, csvPayrollReport.filename);
      dispatch(clearCsvOvertimeReportData());
      setExportType('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csvPayrollReport]);

  useEffect(() => {
    if (v3Data?.V3_PAY_PERIOD_START) {
      dispatch(getPayperiod());
    }
  }, [department, dispatch]);




  return (
    <Paper className="p-4" sx={{ marginTop: '1rem' }}>
      <ReportsHeader title="Payroll Report Details" />

      <Box className="mt-4" sx={{ width: '100%' }}>
        <Box spacing={2} className="my-2">
          {/* Single toolbar row */}
          <Grid container spacing={2} alignItems="flex-end" sx={{ mb: 3 }}>
            {/* Group By */}
            <Grid item>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Group By
              </Typography>
              <Select
                sx={{ height: '36px', minWidth: 160 }}
                value={groupByParam}
                onChange={handleChangeGroupBy}
                renderValue={renderGroupByValue}
              >
                {GROUPBY.map((it) => (
                  <MenuItem key={it.value} value={it.value}>
                    <ListItemText primary={it.title} />
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            {/* Employee */}
            <Grid item sx={{ minWidth: 200 }}>
              <SelectRebuild
                options={employeeList}
                name="emp_code"
                label="Employee"
                multiple
                allOption={{ id: 'ALL', name: 'All' }}
                loading={loadingEmployeeList}
                register={register}
                setValue={setValue}
                watch={watch}
              />
            </Grid>

            {/* Date range pickers — render returns Grid items so must be in a Grid container */}
            <Grid item xs>
              <Grid container spacing={2} alignItems="flex-end">
                {render}
              </Grid>
            </Grid>

            {/* Icon actions */}
            <Grid item>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <Tooltip title="Find">
                  <IconButton onClick={handleFindClick} sx={{ bgcolor: 'primary.main', color: '#fff', borderRadius: 1, '&:hover': { bgcolor: 'primary.dark' } }}>
                    <SearchIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Print">
                  <IconButton onClick={handlePrintSection} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <PrintIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Export CSV">
                  <IconButton
                    onClick={() => handleDownloadReport('payroll_csv')}
                    disabled={loadCsvPayrollReport && exportType === 'payroll_csv'}
                    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                  >
                    <FileDownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Export All CSV">
                  <IconButton
                    onClick={() => handleDownloadReport('payroll_all_csv')}
                    disabled={loadCsvPayrollReport && exportType === 'payroll_all_csv'}
                    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                  >
                    <FileDownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            {loadingPayrollReport && (
              <Box
                sx={{ minHeight: '100px' }}
                className="w-100 h-100 d-flex justify-content-center align-items-center"
              >
                <CircularProgress />
              </Box>
            )}

            <div ref={printRef}>
              {!loadingPayrollReport && tableData.length > 0 &&
                tableData.map(([key, sections], idx) => {
                  const hasScheduled =
                    sections.scheduled && sections.scheduled.length > 0;
                  const hasUnscheduled =
                    sections.unscheduled && sections.unscheduled.length > 0;

                  if (!hasScheduled && !hasUnscheduled) return null;

                  return (
                    <Box key={`emp-${idx}`} sx={{ mt: idx === 0 ? 3 : 4 }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, fontSize: '1rem', mb: 1 }}
                      >
                        {key}
                      </Typography>

                      <TableContainer
                        component={Paper}
                        sx={{ overflowX: 'auto' }}
                      >
                        <Table
                          size="small"
                          sx={{ width: '100%', tableLayout: 'fixed' }}
                        >
                          <TableHead>
                            <TableRow>
                              {headerItems.map(([k, label], hIdx) => (
                                <TableCell
                                  key={`head-${idx}-${hIdx}`}
                                  sx={{
                                    whiteSpace: 'nowrap',
                                    fontWeight: 600,
                                    fontSize: '0.8125rem',
                                    width: COL_WIDTHS[k] || 'auto',
                                  }}
                                >
                                  {label}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>

                          <TableBody>
                            <SectionRows
                              items={sections.scheduled}
                              headerItems={headerItems}
                              sectionLabel="Scheduled"
                            />
                            <SectionRows
                              items={sections.unscheduled}
                              headerItems={headerItems}
                              sectionLabel="Unscheduled"
                            />
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  );
                })}
            </div>

            {!loadingPayrollReport && !tableData.length && (
              <Box>
                <Grid
                  container
                  justifyContent={'center'}
                  spacing={3}
                  className="my-2"
                >
                  <Box py={4}> No data available</Box>
                </Grid>
              </Box>
            )}
            {paginationComponent}
          </Grid>
        </Box>
      </Box>
    </Paper>
  );
};

export default ReportPayrollScreen;
